import React, { ReactElement, useEffect, useState, useRef } from 'react';
import {
  Wrapper,
  Settings,
  Options,
  VisibilityContainer,
  EndingContainer,
  SettingsActions,
  StorySettingsContainer,
  StoryNameRow,
  StoryInfoRow,
  StoryInfoLabel,
  StoryUrlContainer,
  StoryUrlParagraph,
  StoryUrlRow,
  OptionsSubtitle,
  VisibilityOption,
  VisibilityLabels,
  EndingFieldContainer,
  EndingLabelsContainer,
  EndingLabel,
  EndingCaption,
  EndingPreviewImage,
} from './styled';
import { Button } from '@unfold/button';
import { Typography } from '@unfold/typography';
import { Input } from '@unfold/input';
import { Toggle } from '@unfold/toggle';
import { EditIcon, CheckIcon, CheckBigIcon } from '@unfold/icon';
import { Colors } from '@unfold/theme';
import { IStoryModalProps } from './types';
import { format, fromUnixTime } from 'date-fns';
import {
  StoryStatus,
  StoryPrivacy,
  StoryUnfoldPage,
} from '../../../common/types';
import { useStoriesState } from '../../../store';
import { Modal } from '@unfold/modal';
import { formatBytes } from '@unfold/utils';
import endingPreview from '../../images/story-ending.png';

type TVisibilityOption = {
  title: string;
  description: string;
  key: string;
  checked: boolean;
};

const StoryModal: React.FC<IStoryModalProps> = ({ story }) => {
  const [, actions] = useStoriesState();
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(story.title);
  const [unpublishedStory, setUnpublishedStory] = useState<boolean>(false);
  const [publicStory, setPublicStory] = useState<boolean>(false);
  const [privateStory, setPrivateStory] = useState<boolean>(false);
  const [publishLink, setPublishLink] = useState<string>(story.link);
  const [copied, setCopied] = useState<boolean>(false);
  const [isEndingPreviewOpen, setIsEndingPreviewOpen] = useState<boolean>(
    false
  );
  const publishLinkInputRef = useRef<HTMLInputElement>(null);
  const isPublished = story.code === StoryStatus.PUBLISHED;
  const isPrivate = story.privacy === StoryPrivacy.PRIVATE;
  const isPublic = story.privacy === StoryPrivacy.PUBLIC;

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setTitle(e.target.value);
  const handleTitleEdit = (): void => setIsEditingName(true);

  const handleRename = (): void => {
    setIsEditingName(false);
    actions.updateStory(story.uuid, {
      title,
    });
  };

  const handleDelete = (storyId: string): void => {
    actions.deleteStory(storyId);
  };

  const handleVisibilityClick = (key: string): void => {
    let privacy;
    let code;

    if (key === 'unpublished') {
      code = StoryStatus.UNPUBLISHED;
      setUnpublishedStory(true);
      setPublishLink('');
      privacy = undefined;
    } else {
      setUnpublishedStory(false);
      setPublishLink(story.link);
      code = StoryStatus.PUBLISHED;
      if (key === 'public') {
        setPrivateStory(false);
        setPublicStory(true);
        privacy = StoryPrivacy.PUBLIC;
      } else if (key === 'private') {
        setPrivateStory(true);
        setPublicStory(false);
        privacy = StoryPrivacy.PRIVATE;
      }
    }

    //send visibility out to save
    actions.updateStory(story.uuid, {
      code,
      privacy,
    });
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setPublishLink(e.target.value);

  const handlePublishClick = (): void => {
    // TODO: could maybe have a more explicit action to help: actions.publishStory
    actions.updateStory(story.uuid, {
      link: publishLink,
      code: StoryStatus.PUBLISHED,
      privacy: StoryPrivacy.PUBLIC,
    });
  };

  const handleCopyClick = (): void => {
    const copyText = publishLinkInputRef.current;
    if (!copyText) return;

    /* Select the text field */
    copyText.select();
    copyText.setSelectionRange(0, 99999); /* For mobile devices */

    /* Copy the text inside the text field */
    document.execCommand('copy');

    /* Unselect the text field */
    copyText.blur();

    setCopied(true);
    /* Only make the button text change for 2s. */
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUnfoldPageToggle = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    actions.updateStory(story.uuid, {
      // eslint-disable-next-line @typescript-eslint/camelcase
      unfold_page: e.target.checked ? StoryUnfoldPage.ON : StoryUnfoldPage.OFF,
    });
  };
  const handleEndingPreviewClick = (): void => setIsEndingPreviewOpen(true);
  const handleEndingPreviewClose = (): void => setIsEndingPreviewOpen(false);

  const renderVisibilityOption = ({
    key,
    title,
    description,
    checked,
  }: TVisibilityOption): ReactElement => (
    <VisibilityOption
      id={key}
      onClick={handleVisibilityClick.bind(null, key)}
      checked={checked}
    >
      <VisibilityLabels>
        <Typography variant="H500">{title}</Typography>
        <Typography variant="Paragraph">{description}</Typography>
      </VisibilityLabels>
      <CheckBigIcon />
    </VisibilityOption>
  );

  useEffect(() => {
    setPublishLink(isPublished ? story.link : '');
  }, []);
  useEffect(() => setUnpublishedStory(!isPublished), [isPublished]);
  useEffect(() => setPublicStory(isPublic), [isPublic]);
  useEffect(() => setPrivateStory(isPrivate), [isPrivate]);

  return (
    <Wrapper>
      <Settings>
        <StorySettingsContainer>
          <StoryNameRow>
            {isEditingName ? (
              <Input autoFocus appearance="none" height="auto" value={title} onChange={handleTitleChange} />
            ) : (
              <Typography variant="H500">{title}</Typography>
            )}

            <Button
              appearance="bordered"
              iconBefore={isEditingName ? <CheckIcon /> : <EditIcon />}
              width={29}
              height={29}
              fontSize={11}
              onClick={isEditingName ? handleRename : handleTitleEdit}
            />
          </StoryNameRow>
          <StoryInfoRow>
            <StoryInfoLabel>Size</StoryInfoLabel>
            <span>{formatBytes(story.storySize)}</span>
          </StoryInfoRow>
          <StoryInfoRow>
            <StoryInfoLabel>Last Updated</StoryInfoLabel>
            <span>{format(fromUnixTime(story.updated_at), 'MM/dd/yyyy')}</span>
          </StoryInfoRow>
          <StoryInfoRow>
            <StoryInfoLabel>Pages</StoryInfoLabel>
            <span>{story.page_counter}</span>
          </StoryInfoRow>
          <SettingsActions>
            <Button
              appearance="primary"
              width={132}
              fontSize={14}
              fontWeight={500}
              onClick={(): void => handleDelete(story.uuid)}
            >
              Delete story
            </Button>
            {/* <Button
              width={132}
              fontSize={11}
              fontWeight={500}
              isDisabled
            >
              Duplicate
            </Button> */}
          </SettingsActions>
        </StorySettingsContainer>
        <StoryUrlContainer>
          <Typography variant="H500">Web Story</Typography>
          <StoryUrlParagraph>
            <Typography
              variant="Paragraph"
              fontSize={12}
              customColor={Colors.G300}
            >
              Get a URL so you can share your story on the web.
            </Typography>
          </StoryUrlParagraph>
          <StoryUrlRow>
            <Input
              readOnly
              placeholder="stori.es"
              width={180}
              value={publishLink}
              onChange={handleLinkChange}
              ref={publishLinkInputRef}
            />
            {!isPublished ? (
              <Button
                appearance="primary"
                fontSize={14}
                fontWeight={500}
                width={82}
                isDisabled={isPublished}
                onClick={handlePublishClick}
              >
                Publish
              </Button>
            ) : (
              <Button
                appearance="bordered"
                fontSize={14}
                fontWeight={500}
                width={82}
                onClick={handleCopyClick}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            )}
          </StoryUrlRow>
        </StoryUrlContainer>
      </Settings>
      <Options>
        <Typography variant="H500">Web Story Options</Typography>
        <VisibilityContainer>
          <OptionsSubtitle>Visibility</OptionsSubtitle>
          {renderVisibilityOption({
            title: 'Unpublished',
            description: 'Not on the web. Only available on your device.',
            key: 'unpublished',
            checked: unpublishedStory,
          })}
          {renderVisibilityOption({
            title: 'Public',
            description:
              'Published and searchable on Google and other search engines.',
            key: 'public',
            checked: !unpublishedStory && publicStory,
          })}
          {renderVisibilityOption({
            title: 'Private',
            description:
              'Published on web but only available to viewers with link.',
            key: 'private',
            checked: !unpublishedStory && privateStory,
          })}
        </VisibilityContainer>
        <EndingContainer>
          <OptionsSubtitle>Ending</OptionsSubtitle>
          <EndingFieldContainer>
            <EndingLabelsContainer>
              <EndingLabel>{"'Made With Unfold' Final Page"}</EndingLabel>
              <EndingCaption onClick={handleEndingPreviewClick}>
                Click here to preview
              </EndingCaption>
            </EndingLabelsContainer>
            <Toggle onChange={handleUnfoldPageToggle} />
          </EndingFieldContainer>
          <Modal
            isOpen={isEndingPreviewOpen}
            onClose={handleEndingPreviewClose}
            width={289}
            hasPadding={false}
          >
            <EndingPreviewImage src={endingPreview} />
          </Modal>
        </EndingContainer>
      </Options>
    </Wrapper>
  );
};

export default StoryModal;
